import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  ClickAwayListener,
  InputLabel,
  MenuItem,
  FormControl as MuiFormControl,
  Paper,
  Select,
  Slider,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import type { Edge, Node } from '@xyflow/react';
import { Panel, useReactFlow } from '@xyflow/react';

import { logger } from '@/utils/logger';

import { DEFAULT_SPACING_CONFIG, useNodeGeneration } from '../context/node-generation-context';

interface AdvancedControlsProps {
  skipInitialLayout?: boolean;
}

const EDGE_TYPES = [
  { value: 'default', label: 'Default' },
  { value: 'straight', label: 'Straight' },
  { value: 'step', label: 'Step' },
  { value: 'smoothstep', label: 'Smooth Step' },
] as const;

const AdvancedControls: React.FC<AdvancedControlsProps> = ({ skipInitialLayout = false }) => {
  const { spacingConfig, updateSpacingConfig, resetSpacingConfig, applyNodesPositioning } =
    useNodeGeneration();

  const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();
  const [isSliderChanging, setIsSliderChanging] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialMountRef = useRef(skipInitialLayout);

  const applyLayout = useCallback(() => {
    const currentNodes = getNodes();
    const currentEdges = getEdges();

    applyNodesPositioning(currentNodes, currentEdges)
      .then((result: { nodes?: Node[]; edges?: Edge[] } | undefined) => {
        if (result?.nodes && result?.edges) {
          setNodes([...result.nodes]);
          setEdges([...result.edges]);
        }
      })
      .catch((error: unknown) => {
        logger.error('Error applying layout:', error);
      });
  }, [getNodes, getEdges, setNodes, setEdges, applyNodesPositioning]);

  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }

    if (isSliderChanging) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      applyLayout();
    }, 200);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [spacingConfig, applyLayout, isSliderChanging]);

  const handleEdgeTypeChange = (e: SelectChangeEvent<string>) => {
    const newType = e.target.value;
    updateSpacingConfig('edgeType', newType);

    const currentEdges = getEdges();
    const updatedEdges = currentEdges.map((edge: Edge) => ({
      ...edge,
      type: newType,
    }));
    setEdges([...updatedEdges]);
  };

  const handleHorizontalScaleChange = (_e: Event, value: number | number[]) => {
    setIsSliderChanging(true);
    updateSpacingConfig('horizontalScale', typeof value === 'number' ? value : value[0]);
  };

  const handleVerticalScaleChange = (_e: Event, value: number | number[]) => {
    setIsSliderChanging(true);
    updateSpacingConfig('verticalScale', typeof value === 'number' ? value : value[0]);
  };

  const handleSliderCommitted = () => {
    setIsSliderChanging(false);
    applyLayout();
  };

  const handleReset = () => {
    resetSpacingConfig();

    const currentEdges = getEdges();
    const updatedEdges = currentEdges.map((edge: Edge) => ({
      ...edge,
      type: DEFAULT_SPACING_CONFIG.edgeType,
    }));
    setEdges([...updatedEdges]);

    applyLayout();
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Edge & Spacing Controls
      </Typography>
      <Box component="form">
        <MuiFormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="edge-type-label">Edge Type</InputLabel>
          <Select
            labelId="edge-type-label"
            id="edge-type-select"
            name="edgeType"
            value={spacingConfig.edgeType}
            onChange={handleEdgeTypeChange}
            label="Edge Type"
            size="small"
          >
            {EDGE_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </MuiFormControl>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Horizontal Scale: {spacingConfig.horizontalScale.toFixed(1)}
          </Typography>
          <Slider
            id="horizontal-scale-slider"
            name="horizontalScale"
            min={0.5}
            max={3}
            step={0.1}
            value={spacingConfig.horizontalScale}
            onChange={handleHorizontalScaleChange}
            onChangeCommitted={handleSliderCommitted}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Vertical Scale: {spacingConfig.verticalScale.toFixed(1)}
          </Typography>
          <Slider
            id="vertical-scale-slider"
            name="verticalScale"
            min={0.5}
            max={3}
            step={0.1}
            value={spacingConfig.verticalScale}
            onChange={handleVerticalScaleChange}
            onChangeCommitted={handleSliderCommitted}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" size="small" onClick={handleReset}>
            Reset to Default
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

interface CustomAdvancedPanelProps {
  isReadOnly: boolean;
}

export const CustomAdvancedPanel: React.FC<CustomAdvancedPanelProps> = ({ isReadOnly }) => {
  const [showControls, setShowControls] = useState(false);
  const skipInitialLayoutRef = useRef(true);

  if (isReadOnly) return null;

  return (
    <Panel position="top-right" style={{ marginTop: 70, width: 'min-content' }}>
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={(event) => {
          if (!showControls) return;
          const target = event.target as HTMLElement;
          if (target.closest('.MuiPopover-root, .MuiModal-root, .MuiMenu-root')) return;
          setShowControls(false);
        }}
      >
        <Box>
          <Button
            variant="outlined"
            size="small"
            sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
            onClick={() => setShowControls((prev) => !prev)}
          >
            {showControls ? 'Hide Controls' : 'Edge & Scale Controls'}
          </Button>

          {showControls && (
            <Paper
              elevation={3}
              sx={{
                mt: 1,
                p: 1.5,
                width: 250,
                borderRadius: 1,
              }}
            >
              <AdvancedControls skipInitialLayout={skipInitialLayoutRef.current} />
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
    </Panel>
  );
};
